import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts'
import { useState, useMemo } from 'react'
import { DateRange } from 'react-day-picker'
import { format, eachDayOfInterval, startOfDay, subDays } from 'date-fns'
import { supabase } from '@/integrations/supabase/client'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

export interface FeedbackDataPoint {
	date: string
	formattedDate: string
	bugs: number
	features: number
	contacts: number
	reviews: number
	responses: number
	chat: number
	total: number
}

interface FeedbackTimelineChartProps {
	programId: string | null
	title?: string
	height?: number
	className?: string
	dateRange?: DateRange | undefined
}

interface FeedbackTypeConfig {
	tableName: string
	key: string
	name: string
	color: string
}

const FEEDBACK_TYPES: FeedbackTypeConfig[] = [
	{ tableName: 'chat_conversations', key: 'chat', name: 'Chat', color: 'hsl(262 83% 58%)' },
	{ tableName: 'bug_reports', key: 'bugs', name: 'Bugs', color: 'hsl(0 84% 60%)' },
	{ tableName: 'reviews', key: 'reviews', name: 'Reviews', color: 'hsl(221 83% 53%)' },
	{ tableName: 'feature_requests', key: 'features', name: 'Features', color: 'hsl(142 71% 45%)' },
	{ tableName: 'contact_messages', key: 'contacts', name: 'Mailbox', color: 'hsl(173 80% 40%)' },
	{ tableName: 'responses', key: 'responses', name: 'Responses', color: 'hsl(38 92% 50%)' },
]

const formatDate = (dateString: string): string => {
	const date = new Date(dateString)
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const formatTooltipDate = (dateString: string): string => {
	const date = new Date(dateString)
	return date.toLocaleDateString('en-US', {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		year: 'numeric'
	})
}

async function fetchFeedbackCountsByDay(
	tableName: string,
	programId: string,
	fromDate: Date,
	toDate: Date
): Promise<Record<string, number>> {
	try {
		const { data, error } = await supabase
			.from(tableName as any)
			.select('created_at')
			.eq('program_id', programId)
			.gte('created_at', fromDate.toISOString())
			.lte('created_at', toDate.toISOString())

		if (error) {
			console.error(`Error fetching ${tableName}:`, error)
			return {}
		}

		if (!data) {
			return {}
		}

		const countsByDay: Record<string, number> = {}
		data.forEach((item: any) => {
			if (item?.created_at) {
				const dateKey = format(startOfDay(new Date(item.created_at)), 'yyyy-MM-dd')
				countsByDay[dateKey] = (countsByDay[dateKey] || 0) + 1
			}
		})

		return countsByDay
	} catch (err) {
		console.error(`Exception fetching ${tableName}:`, err)
		return {}
	}
}

const CustomTooltip = ({ active, payload, label }: any) => {
	if (active && payload && payload.length) {
		const visiblePayload = payload.filter((entry: any) => entry.value > 0)
		const total = visiblePayload.reduce((sum: number, item: any) => sum + item.value, 0)
		const fullDate = payload[0]?.payload?.date

		return (
			<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-3 sm:p-4 shadow-2xl backdrop-blur-xl animate-in fade-in-0 zoom-in-95 duration-200 max-w-[90vw] sm:max-w-none">
				<p className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2 sm:mb-3 pb-2 border-b border-slate-100 dark:border-slate-800">
					{fullDate ? formatTooltipDate(fullDate) : label}
				</p>
				<div className="space-y-1.5 sm:space-y-2.5">
					{visiblePayload.map((entry: any, index: number) => (
						<div key={index} className="flex items-center justify-between gap-3 sm:gap-4 group">
							<div className="flex items-center gap-1.5 sm:gap-2">
								<div
									className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm shadow-sm transition-transform group-hover:scale-110"
									style={{ backgroundColor: entry.color }}
								/>
								<p className="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 capitalize">
									{entry.dataKey}
								</p>
							</div>
							<p className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 tabular-nums">
								{entry.value}
							</p>
						</div>
					))}
					{visiblePayload.length > 1 && (
						<div className="border-t border-slate-100 dark:border-slate-800 pt-1.5 sm:pt-2.5 mt-1.5 sm:mt-2">
							<div className="flex items-center justify-between">
								<p className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100">
									Total
								</p>
								<p className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 tabular-nums">
									{total}
								</p>
							</div>
						</div>
					)}
				</div>
			</div>
		)
	}
	return null
}

const CustomLegend = ({
	feedbackTypes,
	hiddenTypes,
	onToggle
}: {
	feedbackTypes: FeedbackTypeConfig[]
	hiddenTypes: Set<string>
	onToggle: (key: string) => void
}) => {
	return (
		<div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 px-3 sm:px-6 pb-3 sm:pb-4" role="list">
			{feedbackTypes.map((type) => {
				const isHidden = hiddenTypes.has(type.key)
				return (
					<button
						key={type.key}
						onClick={() => onToggle(type.key)}
						className={cn(
							"flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg transition-all duration-200",
							"hover:bg-slate-100 dark:hover:bg-slate-800",
							"focus:outline-none focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-600",
							isHidden && "opacity-40"
						)}
						aria-label={`${isHidden ? 'Show' : 'Hide'} ${type.name} data`}
						aria-pressed={!isHidden}
						role="listitem"
					>
						<div
							className={cn(
								"w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm transition-all duration-200",
								isHidden && "opacity-50"
							)}
							style={{ backgroundColor: type.color }}
						/>
						<span className={cn(
							"text-xs sm:text-sm font-medium transition-all duration-200",
							isHidden
								? "text-slate-400 dark:text-slate-500 line-through"
								: "text-slate-700 dark:text-slate-300"
						)}>
							{type.name}
						</span>
					</button>
				)
			})}
		</div>
	)
}

export function FeedbackTimelineChart({
	programId,
	title = "Feedback Timeline",
	height = 300,
	className = "",
	dateRange
}: FeedbackTimelineChartProps) {
	const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set())

	const effectiveDateRange = useMemo(() => {
		if (dateRange?.from && dateRange?.to) {
			return dateRange
		}
		const today = new Date()
		return {
			from: subDays(today, 30),
			to: today
		}
	}, [dateRange?.from, dateRange?.to])

	const fromDateString = effectiveDateRange?.from ? effectiveDateRange.from.toISOString() : null
	const toDateString = effectiveDateRange?.to ? effectiveDateRange.to.toISOString() : null

	const { data: feedbackData = [], isLoading, error } = useQuery({
		queryKey: ['feedbackTimelineChart', programId, fromDateString, toDateString],
		queryFn: async () => {
			if (!programId || !effectiveDateRange?.from || !effectiveDateRange?.to) {
				return []
			}

			const fromDate = startOfDay(effectiveDateRange.from)
			const toDate = new Date(effectiveDateRange.to)
			toDate.setHours(23, 59, 59, 999)

			const allDays = eachDayOfInterval({ start: fromDate, end: toDate })
			const feedbackByType: Record<string, Record<string, number>> = {}

			for (const feedbackType of FEEDBACK_TYPES) {
				const counts = await fetchFeedbackCountsByDay(
					feedbackType.tableName,
					programId,
					fromDate,
					toDate
				)
				feedbackByType[feedbackType.key] = counts
			}

			const dataPoints: FeedbackDataPoint[] = allDays.map((day) => {
				const dateKey = format(day, 'yyyy-MM-dd')
				const chat = feedbackByType['chat']?.[dateKey] || 0
				const bugs = feedbackByType['bugs']?.[dateKey] || 0
				const reviews = feedbackByType['reviews']?.[dateKey] || 0
				const features = feedbackByType['features']?.[dateKey] || 0
				const contacts = feedbackByType['contacts']?.[dateKey] || 0
				const responses = feedbackByType['responses']?.[dateKey] || 0

				return {
					date: dateKey,
					formattedDate: formatDate(dateKey),
					chat,
					bugs,
					reviews,
					features,
					contacts,
					responses,
					total: chat + bugs + reviews + features + contacts + responses,
				}
			})

			return dataPoints
		},
		enabled: !!programId && !!effectiveDateRange?.from && !!effectiveDateRange?.to,
		retry: 1,
	})

	const handleLegendClick = (key: string) => {
		setHiddenTypes(prev => {
			const newSet = new Set(prev)
			if (newSet.has(key)) {
				newSet.delete(key)
			} else {
				newSet.add(key)
			}
			return newSet
		})
	}

	const feedbackTypes = FEEDBACK_TYPES.map((type) => ({
		key: type.key,
		name: type.name,
		color: type.color,
		radius: type.key === 'responses' ? [4, 4, 0, 0] as [number, number, number, number] : [0, 0, 0, 0] as [number, number, number, number]
	}))

	if (error) {
		console.error('Error loading feedback timeline chart:', error)
	}

	if (isLoading) {
		return (
			<Card className={cn("h-full flex flex-col", className)}>
				<CardHeader className="pb-2 sm:pb-3">
					<CardTitle className="text-sm sm:text-base font-semibold">{title}</CardTitle>
				</CardHeader>
				<CardContent className="flex-1 flex flex-col p-4 sm:p-6 pt-0">
					<div className="flex-1 flex items-center justify-center" style={{ minHeight: `${height}px` }}>
						<div className="flex flex-col items-center gap-3 sm:gap-4">
							<Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-slate-400" />
							<p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Loading chart data...</p>
						</div>
					</div>
				</CardContent>
			</Card>
		)
	}

	// Empty state
	if (!feedbackData || feedbackData.length === 0) {
		return (
			<Card className={cn("h-full flex flex-col", className)}>
				<CardHeader className="pb-2 sm:pb-3">
					<CardTitle className="text-sm sm:text-base font-semibold">{title}</CardTitle>
				</CardHeader>
				<CardContent className="flex-1 flex flex-col p-4 sm:p-6 pt-0">
					<div className="flex-1 flex items-center justify-center" style={{ minHeight: `${height}px` }}>
						<div className="flex flex-col items-center gap-2 sm:gap-3 text-center">
							<div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
								<BarChart className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400" />
							</div>
							<div>
								<p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100">No feedback data</p>
								<p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
									No feedback data available for this period
								</p>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card className={cn("h-full flex flex-col", className)}>
			<CardHeader className="pb-2 sm:pb-3">
				<CardTitle className="text-sm sm:text-base font-semibold">{title}</CardTitle>
			</CardHeader>
			<CardContent className="flex-1 flex flex-col p-3 sm:p-6 pt-0">
				<div className="flex-1" style={{ minHeight: `${height}px`, height: `${height}px` }}>
					<ResponsiveContainer width="100%" height="100%" minHeight={height}>
						<BarChart
							data={feedbackData}
							margin={{
								top: 10,
								right: 10,
								left: 0,
								bottom: 5,
							}}
						>
							<defs>
								{feedbackTypes.map((type) => (
									<linearGradient key={type.key} id={`gradient-${type.key}`} x1="0" y1="0" x2="0" y2="1">
										<stop offset="0%" stopColor={type.color} stopOpacity={0.9} />
										<stop offset="100%" stopColor={type.color} stopOpacity={0.7} />
									</linearGradient>
								))}
							</defs>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="hsl(var(--muted-foreground))"
								opacity={0.2}
								vertical={false}
							/>
							<XAxis
								dataKey="formattedDate"
								axisLine={false}
								tickLine={false}
								tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
								tickMargin={8}
								height={35}
								interval="preserveStartEnd"
							/>
							<YAxis
								axisLine={false}
								tickLine={false}
								tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
								tickMargin={8}
								width={35}
								tickFormatter={(value) => {
									if (value >= 1000) {
										return `${(value / 1000).toFixed(1)}k`
									}
									return value.toString()
								}}
							/>
							<Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.1 }} />
							{feedbackTypes.map((type) => (
								<Bar
									key={type.key}
									dataKey={type.key}
									stackId="feedback"
									fill={`url(#gradient-${type.key})`}
									name={type.name}
									radius={type.radius}
									hide={hiddenTypes.has(type.key)}
									animationDuration={800}
									animationBegin={0}
								/>
							))}
						</BarChart>
					</ResponsiveContainer>
				</div>
				<CustomLegend
					feedbackTypes={FEEDBACK_TYPES}
					hiddenTypes={hiddenTypes}
					onToggle={handleLegendClick}
				/>
			</CardContent>
		</Card>
	)
}
